// Unpublished Work © 2024 Deere & Company.
import { Search } from "@deere/fuel-react";
import { SxProps } from "@mui/material";
import { ChangeEvent, KeyboardEvent, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { sanitizeInputText } from "../../../../utils/sanitize";

const ControlledHeaderFilter = ({
  inputRef,
  dataTestId,
  value,
  onChange,
  onClear,
  disabled,
  placeHolder,
  sx,
}: {
  inputRef: RefObject<unknown>;
  dataTestId: string;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  disabled?: boolean;
  placeHolder?: string;
  sx?: SxProps;
}) => {
  const { t } = useTranslation("common");
  const placeholder: string = placeHolder ?? t("filter.search.placeholder");

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInputText(event.target.value);
    onChange(sanitizedValue);
    preserveCursorPosition(event, inputRef);
  };

  return (
    <Search
      sx={sx}
      inputRef={inputRef}
      data-testid={dataTestId}
      placeholder={placeholder}
      disabled={disabled}
      value={value}
      onChange={handleInputChange}
      onClear={onClear}
      onEnter={(event) => event.preventDefault()}
      onKeyDown={preserveKeyboardScrolling}
    />
  );
};

export default ControlledHeaderFilter;

function preserveCursorPosition(
  event: ChangeEvent<HTMLInputElement>,
  inputRef: RefObject<unknown>
) {
  const cursorPosition = event.target.selectionStart ?? 0;
  setTimeout(() => {
    if (inputRef?.current) {
      const inputElement = inputRef.current as HTMLInputElement;

      inputElement.setSelectionRange(cursorPosition, cursorPosition);

      if (inputElement.scrollWidth > inputElement.clientWidth) {
        const currentInputValue = inputElement.value.length;
        const widthOfEachCharacter =
          inputElement.scrollWidth / Math.max(currentInputValue, 1);
        const cursorPositionInWidth =
          cursorPosition * widthOfEachCharacter - inputElement.clientWidth;
        const offsetForVisibility = inputElement.clientWidth / 2;

        inputElement.scrollLeft = cursorPositionInWidth + offsetForVisibility;
      }
    }
  }, 10);
}

const preserveKeyboardScrolling = (event: KeyboardEvent<HTMLInputElement>) => {
  if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
    event.stopPropagation();
  }
};
